import React, { useState, useCallback } from 'react';
import { Table, Button, message, Space, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import { SortAscendingOutlined, SortDescendingOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import EditableDrawer from '../../../../../components/EditableDrawer';
import DetailFilter from './DetailFilter';
import { GENERATE_SLA_DETAIL_DOWNLOAD_REPORT, GenerateSlaDetailDownloadReportVariables, GenerateSlaDetailDownloadReportResponse } from '../graphql/mutations/generateSlaDetailDownloadReport';
import { GENERATE_SLA_DETAIL_DOWNLOAD_REPORT_FOR_DSP, GenerateSlaDetailDownloadReportForDspVariables, GenerateSlaDetailDownloadReportForDspResponse } from '../graphql/mutations/generateSlaDetailDownloadReportForDsp';
import ParcelsDownloadModal from '../../../parcels/components/ParcelsDownloadModal';
import { DeliveryProgressMonitoringOfSlaDetailFilterType } from '../graphql/queries/getDeliveryProgressMonitoringOfSlaList';
import { ARRIVAL_POINT_TYPE_WHS } from '../constants';
import { DriverFilterOption, isUnassignedDriverOnly, toDriverIds } from '../../../../../utils/driverFilterOption';
import { UndeliveredLevel1, UndeliveredLevel2 } from '../undeliveredAttribution';

export interface DetailData {
    id?: string; // 主键ID（用于选中数据下载）
    dsp_name: string;
    /** Hub 详情表格展示用，parcel 页无此字段 */
    warehouse_name?: string;
    area_name: string;
    driver_id: number;
    driver_name: string;
    last_operate_dsp_name?: string; // 最后一次操作DSP名称
    last_operate_driver_name?: string; // 最后一次操作司机名称
    last_assign_dsp_name?: string; // 最后一次分配DSP名称
    last_assign_driver_name?: string; // 最后一次分配司机名称
    task_date: string;
    tracking_number: string;
    status: string;
    status_text?: string; // 状态文本
    /** 未妥投一级归因枚举名（v2）：DRIVER_SIDE / WAREHOUSE_SIDE / HANDOVER_DIFF / RETURN；已妥投为 undefined */
    undelivered_reason_level_1?: string;
    /** 未妥投二级归因枚举名（v2）：SlaUndeliveredReasonLevel2 的 15 项，已妥投为 undefined */
    undelivered_reason_level_2?: string;
    order_id: number;
}

interface SlaDetailDrawerProps {
    visible: boolean;
    title: string;
    data: DetailData[];
    columns: any[];
    loading?: boolean;
    onClose: () => void;
    onSortChange?: (sortBy: string, sortDirection: 'ASC' | 'DESC') => void;
    currentSortBy?: string;
    currentSortDirection?: 'ASC' | 'DESC';
    pagination?: { page: number; size: number; total: number };
    onPageChange?: (page: number, pageSize: number) => void;
    detailFilters?: {
        selectedDsps: string[];
        selectedAreas: string[];
        selectedDrivers: string[];
        selectedStatuses: string[];
        selectedWarehouses?: string[];
        undeliveredReasonLevel1?: UndeliveredLevel1;
        undeliveredReasonLevel2?: UndeliveredLevel2;
    };
    onDetailFiltersChange?: (filters: {
        selectedDsps: string[];
        selectedAreas: string[];
        selectedDrivers: string[];
        selectedStatuses: string[];
        selectedWarehouses?: string[];
        undeliveredReasonLevel1?: UndeliveredLevel1;
        undeliveredReasonLevel2?: UndeliveredLevel2;
    }) => void;
    dsps?: Array<{ id: number; name: string }>;
    areas?: Array<{ id: number; name: string }>;
    drivers?: DriverFilterOption[];
    // 下载/查询用日期范围，与主页筛选一致
    arrivalDateStart: string;
    arrivalDateEnd: string;
    warehouseId?: string;
    arrivalPointType?: number; // 0-whs，1-hub
    /** 一级页面选中的城市，导出/查询时一并传参 */
    selectedCities?: string[];
    statusField?: string;
    lastDetailContext?: { type: string; record: any; statusField?: string } | null;
    currentDspId?: number; // 当前DSP ID（DSP经理角色）
    dspMode?: boolean; // 是否为DSP经理
    /** Hub 页：'whs' 时显示 WHS 下拉不显示 DSP，'dsp' 时显示 DSP 不显示 WHS；parcel 页不传则显示 DSP */
    detailDimension?: 'whs' | 'dsp';
    /** Hub 页 WHS 维度下可选的仓库列表（id+name） */
    warehouses?: Array<{ id: number; name: string }>;
    /** 抽屉内司机远程搜索（与主列表同口径，走主库按需查询） */
    onDriverSearch?: (keyword: string) => void;
    driverOptionsLoading?: boolean;
    /** 点击运单号打开包裹详情（与 hub-daily-task-monitoring 一致） */
    onOpenParcelDetail?: (orderId: number) => void;
    /** Hub 页必传：枢纽 ID，与详情查询一致，导出时一并传参否则导出数据量与前端 total 不一致 */
    hubId?: number;
}

const SlaDetailDrawer: React.FC<SlaDetailDrawerProps> = ({
    visible,
    title,
    data,
    columns,
    loading = false,
    onClose,
    onSortChange,
    currentSortBy = 'createdAt',
    currentSortDirection = 'DESC',
    pagination,
    onPageChange,
    detailFilters,
    onDetailFiltersChange,
    dsps = [],
    areas = [],
    drivers = [],
    arrivalDateStart,
    arrivalDateEnd,
    warehouseId,
    arrivalPointType = ARRIVAL_POINT_TYPE_WHS,
    selectedCities,
    statusField,
    lastDetailContext,
    currentDspId,
    dspMode = false,
    detailDimension,
    warehouses = [],
    onDriverSearch,
    driverOptionsLoading,
    onOpenParcelDetail,
    hubId
}) => {
    const { t } = useTranslation();
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [generateReportMutation] = useMutation<
        GenerateSlaDetailDownloadReportResponse | GenerateSlaDetailDownloadReportForDspResponse,
        GenerateSlaDetailDownloadReportVariables | GenerateSlaDetailDownloadReportForDspVariables
    >(dspMode ? GENERATE_SLA_DETAIL_DOWNLOAD_REPORT_FOR_DSP : GENERATE_SLA_DETAIL_DOWNLOAD_REPORT);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState<string>('');
    const DOWNLOAD_COOLDOWN = 5000; // 5秒冷却时间
    const [cooldownRemaining, setCooldownRemaining] = useState(0);
    
    // 从localStorage获取上次下载时间
    const getLastDownloadTime = (): number => {
        const stored = localStorage.getItem('slaDetailDownloadLastTime');
        return stored ? parseInt(stored, 10) : 0;
    };
    
    const setLastDownloadTime = (time: number) => {
        localStorage.setItem('slaDetailDownloadLastTime', time.toString());
    };
    
    // 下载冷却时间倒计时
    React.useEffect(() => {
        const interval = setInterval(() => {
            const currentTime = Date.now();
            const lastTime = getLastDownloadTime();
            const remaining = Math.max(0, DOWNLOAD_COOLDOWN - (currentTime - lastTime));
            setCooldownRemaining(Math.ceil(remaining / 1000));
            
            if (remaining <= 0) {
                setDownloadLoading(false);
                localStorage.removeItem('slaDetailDownloadLastTime');
            }
        }, 1000);
        
        return () => clearInterval(interval);
    }, [DOWNLOAD_COOLDOWN]);
    
    // 当抽屉关闭时，重置选中状态
    React.useEffect(() => {
        if (!visible) {
            setSelectedRowKeys([]);
        }
    }, [visible]);
    
    // 构建导出过滤器（状态优先使用抽屉内选择：单选用 statusField，多选用 statusFields）
    const buildExportFilter = useCallback((): DeliveryProgressMonitoringOfSlaDetailFilterType => {
        // 仓库 ID 解析规则：
        // - Hub 页面（传了 hubId）：
        //   * 有选仓库用选中的
        //   * 未选时用 hub 下全部仓库
        //   * 若仍为空则兜底用 hubId（避免传 [0] 或 [] 导致查不到数据）
        // - Parcel 页面（未传 hubId）：
        //   * 有选仓库用选中的
        //   * 否则若有 warehouses 列表则用列表
        //   * 再否则兜底用单个 warehouseId（即 whsId）
        const resolveExportWarehouseIds = (
            selected: string[] | undefined,
            allWarehouses: Array<{ id: number; name: string }> | undefined,
            hubIdValue: number | undefined,
            whsIdFallback?: string
        ): number[] => {
            const toNum = (ids: string[]): number[] =>
                ids.map((id) => parseInt(String(id), 10)).filter((n) => !Number.isNaN(n));

            // Hub 页面：使用 hub 下 whs + hubId 兜底
            if (hubIdValue != null) {
                const hubWarehouseIds = (allWarehouses ?? []).map((w) => String(w.id));
                const selectedIds = selected && selected.length > 0 ? selected : hubWarehouseIds;
                let ids = toNum(selectedIds);
                if (ids.length === 0 || (ids.length === 1 && ids[0] === 0)) {
                    ids = toNum(hubWarehouseIds);
                }
                if (ids.length === 0 || (ids.length === 1 && ids[0] === 0)) {
                    ids = [hubIdValue];
                }
                return ids;
            }

            // Parcel 页面：优先选中仓库，其次仓库列表，最后 whsId 兜底
            if (selected && selected.length > 0) {
                return toNum(selected);
            }
            if (allWarehouses && allWarehouses.length > 0) {
                return allWarehouses.map((w) => w.id).filter((n) => !Number.isNaN(n));
            }
            if (whsIdFallback) {
                const n = parseInt(whsIdFallback, 10);
                return Number.isNaN(n) ? [] : [n];
            }
            return [];
        };

        const currentSelectedDsps = detailFilters?.selectedDsps || [];
        const currentSelectedAreas = detailFilters?.selectedAreas || [];
        const currentSelectedDrivers = detailFilters?.selectedDrivers || [];
        const currentSelectedStatuses = detailFilters?.selectedStatuses || [];
        // 与详情查询一致：有选状态传单/多选，清空状态时不传，后端返回所有状态数据
        const hasStatusSelection = currentSelectedStatuses.length > 0;
        const effectiveStatusField = hasStatusSelection && currentSelectedStatuses.length === 1
            ? currentSelectedStatuses[0]
            : undefined;
        const effectiveStatusFields = hasStatusSelection && currentSelectedStatuses.length > 1
            ? currentSelectedStatuses
            : undefined;

        let selectedDspIds: number[] | undefined;
        if (currentSelectedDsps.length > 0) {
            selectedDspIds = currentSelectedDsps.map(dspName => {
                const dsp = dsps.find((d: any) => d.name === dspName);
                return dsp ? dsp.id : null;
            }).filter(id => id !== null) as number[];
        }

        let selectedAreaIds: number[] | undefined;
        if (currentSelectedAreas.length > 0) {
            selectedAreaIds = currentSelectedAreas.map(areaName => {
                const area = areas.find((a: any) => a.name === areaName);
                return area ? area.id : null;
            }).filter(id => id !== null) as number[];
        }

        const driverUnassignedOnly = isUnassignedDriverOnly(currentSelectedDrivers);
        const selectedDriverIds = driverUnassignedOnly ? undefined : toDriverIds(currentSelectedDrivers);

        // 与 GetDeliveryProgressMonitoringOfSlaDetailList 一致：有选仓库用所选(id 数组)，无选且 Hub 页用该 hub 下全部仓库，否则用单个 warehouseId
        const selectedWhs = detailFilters?.selectedWarehouses;
        const hasWarehouseSelection = (selectedWhs?.length ?? 0) > 0;
        const warehouseIdsExport = resolveExportWarehouseIds(
            hasWarehouseSelection ? selectedWhs : undefined,
            Array.isArray(warehouses) ? warehouses : undefined,
            hubId,
            warehouseId
        );
        return {
            arrivalDateStart,
            arrivalDateEnd,
            hubId,
            warehouseIds: warehouseIdsExport,
            dspIds: selectedDspIds,
            areaIds: selectedAreaIds,
            driverIds: selectedDriverIds,
            driverIdNullOnly: driverUnassignedOnly ? true : undefined,
            cityNames: selectedCities?.length ? selectedCities : undefined,
            trackingNumber: undefined,
            statusField: effectiveStatusField,
            statusFields: effectiveStatusFields,
            ids: undefined,
            arrivalPointType,
            undeliveredReasonLevel1: detailFilters?.undeliveredReasonLevel1,
            undeliveredReasonLevel2: detailFilters?.undeliveredReasonLevel2,
        };
    }, [arrivalDateStart, arrivalDateEnd, hubId, warehouseId, arrivalPointType, selectedCities, detailFilters, dsps, areas, drivers, warehouses]);
    
    // 下载详情报表
    const handleDownloadDetails = async () => {
        const currentTime = Date.now();
        const lastTime = getLastDownloadTime();
        
        // 检查冷却时间
        if (currentTime - lastTime < DOWNLOAD_COOLDOWN) {
            const remainingTime = Math.ceil((DOWNLOAD_COOLDOWN - (currentTime - lastTime)) / 1000);
            message.warning(`Please wait ${remainingTime} seconds before trying again to avoid duplicate requests`);
            return;
        }
        
        // 检查是否正在生成
        if (downloadLoading) {
            message.warning('Report is being generated, please do not click repeatedly');
            return;
        }

        // 无数据时直接返回：后端会把「上游查询结果」拼成单号串下传，
        // 空串会让下游 `if (tokens.isNotEmpty())` 整段跳过，退化为「不按单号过滤」而导出全表订单
        if (selectedRowKeys.length === 0 && (pagination?.total ?? 0) === 0) {
            message.warning(t('wms.exceptionorder.message.noDataToDownload'));
            return;
        }
        
        try {
            setDownloadLoading(true);
            setLastDownloadTime(currentTime);
            
            // 获取用户时区
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            
            // 构建查询过滤器
            let queryFilter: DeliveryProgressMonitoringOfSlaDetailFilterType;
            
            // 如果有选中的行，只下载选中的数据（只使用 ids，忽略其他过滤条件）；warehouseIds 与 buildExportFilter 规则一致
            if (selectedRowKeys.length > 0) {
                const selWhs = detailFilters?.selectedWarehouses;
                const hasSel = (selWhs?.length ?? 0) > 0;
                // 选中行导出时也复用与 buildExportFilter 一致的仓库 ID 解析规则
                const idsWarehouseIds = (() => {
                    const toNum = (ids: string[]): number[] =>
                        ids.map((id) => parseInt(String(id), 10)).filter((n) => !Number.isNaN(n));

                    if (hubId != null) {
                        const hubWarehouseIds = (Array.isArray(warehouses) ? warehouses : []).map((w) => String(w.id));
                        const selectedIds = hasSel && selWhs ? selWhs : hubWarehouseIds;
                        let ids = toNum(selectedIds);
                        if (ids.length === 0 || (ids.length === 1 && ids[0] === 0)) {
                            ids = toNum(hubWarehouseIds);
                        }
                        if (ids.length === 0 || (ids.length === 1 && ids[0] === 0)) {
                            ids = [hubId];
                        }
                        return ids;
                    }

                    if (hasSel && selWhs) {
                        return toNum(selWhs);
                    }
                    if (Array.isArray(warehouses) && warehouses.length > 0) {
                        return warehouses.map((w) => w.id).filter((n) => !Number.isNaN(n));
                    }
                    if (warehouseId) {
                        const n = parseInt(warehouseId, 10);
                        return Number.isNaN(n) ? [] : [n];
                    }
                    return [];
                })();
                queryFilter = {
                    arrivalDateStart,
                    arrivalDateEnd,
                    hubId,
                    warehouseIds: idsWarehouseIds,
                    ids: selectedRowKeys.map(key => String(key)),
                    dspIds: undefined,
                    areaIds: undefined,
                    driverIds: undefined,
                    trackingNumber: undefined,
                    statusField: undefined,
                    arrivalPointType,
                };
            } else {
                queryFilter = buildExportFilter();
            }
            
            const response = await generateReportMutation({
                variables: {
                    filter: queryFilter,
                    timezone: userTimezone
                }
            });
            
            const fileName = dspMode
                ? (response.data as GenerateSlaDetailDownloadReportForDspResponse | undefined)?.generateSlaDetailDownloadReportForDsp
                : (response.data as GenerateSlaDetailDownloadReportResponse | undefined)?.generateSlaDetailDownloadReport;
            if (fileName) {
                setGeneratedFileName(fileName);
                setDownloadCompleteModalVisible(true);
            } else {
                message.error('Failed to start report generation task');
            }
        } catch (error: any) {
            console.error('Report generation failed:', error);
            let errorMessage = 'Report generation failed, please try again later';
            if (error?.graphQLErrors && error.graphQLErrors.length > 0) {
                const graphQLError = error.graphQLErrors[0];
                errorMessage = graphQLError.extensions?.message || graphQLError.message || errorMessage;
            } else if (error?.message) {
                errorMessage = error.message;
            }
            message.error(errorMessage);
            setDownloadLoading(false);
        }
    };
    // 处理排序点击
    const handleSortClick = (field: string) => {
        let newDirection: 'ASC' | 'DESC' = 'ASC'; // 首次点击默认为升序
        
        // 如果点击的是当前排序字段，则切换排序方向
        if (currentSortBy === field) {
            newDirection = currentSortDirection === 'DESC' ? 'ASC' : 'DESC';
        }
        
        onSortChange?.(field, newDirection);
    };

    // 渲染排序图标
    const renderSortIcon = (field: string) => {
        if (currentSortBy !== field) {
            return <SortDescendingOutlined className="ml-1 cursor-pointer text-gray-400" />;
        }
        
        return currentSortDirection === 'DESC' 
            ? <SortAscendingOutlined className="ml-1 cursor-pointer text-blue-500" />
            : <SortDescendingOutlined className="ml-1 cursor-pointer text-blue-500" />;
    };

    // 复制选中行的运单号到剪贴板（参照 daily-task-monitoring 抽屉）
    const copyTrackingNumbers = useCallback(async () => {
        if (!selectedRowKeys || selectedRowKeys.length === 0) {
            message.warning(t('central.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        const keySet = new Set(selectedRowKeys.map(k => String(k)));
        const trackingNumbers = data
            .filter(record => keySet.has(String(record.id ?? '')) || keySet.has(String(record.tracking_number ?? '')))
            .map(record => (record.tracking_number ?? '').trim())
            .filter((tracking): tracking is string => Boolean(tracking && tracking.length > 0));
        if (trackingNumbers.length === 0) {
            message.warning(t('central.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        const text = trackingNumbers.join('\n');
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            message.success(t('common.messages.copied'));
        } catch (error) {
            console.error('Copy tracking numbers failed:', error);
            message.error(t('common.messages.copyFailed'));
        }
    }, [selectedRowKeys, data]);

    // 若有 onOpenParcelDetail，将运单号列改为可点击打开包裹详情（与 hub-daily-task-monitoring 一致）
    const effectiveColumns = React.useMemo(() => {
        if (!onOpenParcelDetail || !columns?.length) return columns;
        return columns.map((col: any) => {
            if (col.key === 'tracking_number' || col.dataIndex === 'tracking_number') {
                return {
                    ...col,
                    render: (text: string, record: DetailData) => (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                if (record.order_id) onOpenParcelDetail(record.order_id);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {text ?? '-'}
                        </a>
                    )
                };
            }
            return col;
        });
    }, [columns, onOpenParcelDetail]);

    return (
        <>
            <EditableDrawer
                title={title}
                visible={visible}
                onClose={onClose}
                width={window.innerWidth * 0.7}
                showDefaultFooter={false}
                zIndex={999}
            >
                {detailFilters !== undefined && onDetailFiltersChange && (
                    <div className="mb-4 pb-4">
                        <Row gutter={[16, 16]} align="middle">
                            <Col flex="auto">
                                <DetailFilter
                                    filters={detailFilters}
                                    onFiltersChange={onDetailFiltersChange}
                                    dsps={dsps}
                                    areas={areas}
                                    drivers={drivers}
                                    viewMode="area"
                                    noWrapper={true}
                                    currentDspId={currentDspId}
                                    dspMode={dspMode}
                                    detailDimension={detailDimension}
                                    warehouses={warehouses}
                                    onDriverSearch={onDriverSearch}
                                    driverOptionsLoading={driverOptionsLoading}
                                />
                            </Col>
                        </Row>
                        <Row gutter={[16, 4]} align="middle" justify="end" style={{ marginTop: 8 }}>
                            <Col flex="none">
                                <Space direction="vertical" size={4} className="items-end">
                                    <Space>
                                        {selectedRowKeys.length > 0 && (
                                            <span style={{ marginRight: 8 }}>
                                                {t('wms.delivery.deliveryCompletedSummary.selectedCount', { count: selectedRowKeys.length })}
                                            </span>
                                        )}
                                        <Button
                                            type="primary"
                                            onClick={copyTrackingNumbers}
                                            className="w-auto min-w-24"
                                        >
                                            <span className="font-bold">{t('common.buttons.copyTracking')}</span>
                                        </Button>
                                        <Button
                                            type="primary"
                                            onClick={handleDownloadDetails}
                                            disabled={downloadLoading || cooldownRemaining > 0}
                                            loading={downloadLoading}
                                        >
                                            <span className="font-bold">
                                                {cooldownRemaining > 0
                                                    ? `${selectedRowKeys.length > 0 ? t('wms.delivery.deliveryCompletedSummary.exportSelected') : t('components.button.export')} ${cooldownRemaining}s`
                                                    : selectedRowKeys.length > 0
                                                    ? t('wms.delivery.deliveryCompletedSummary.exportSelected')
                                                    : t('components.button.export')}
                                            </span>
                                        </Button>
                                    </Space>
                                    <span className="text-xs text-gray-500">{t('wms.delivery.parcelSlaMonitoring.detailDrawer.exportFieldsAlignWithParcels')}</span>
                                </Space>
                            </Col>
                        </Row>
                    </div>
                )}
                <Table
                    dataSource={data}
                    columns={effectiveColumns}
                    rowKey={(record) => record.id || record.tracking_number}
                    loading={loading}
                    rowSelection={{
                        selectedRowKeys,
                        onChange: (keys) => {
                            setSelectedRowKeys(keys);
                        }
                    }}
                    pagination={
                        pagination
                            ? {
                                  current: pagination.page,
                                  pageSize: pagination.size,
                                  total: pagination.total,
                                  showSizeChanger: true,
                                  showQuickJumper: true,
                                  showTotal: (total) => t('table.pagination.totalItems', { total }),
                                  onChange: (page, pageSize) => {
                                      // 确保 pageSize 始终传递，如果没有则使用当前的 pagination.size
                                      const finalPageSize = pageSize ?? pagination.size;
                                      onPageChange?.(page, finalPageSize);
                                  }
                              }
                            : false
                    }
                    scroll={{ x: 800 }}
                    size="small"
                />
            </EditableDrawer>
            {/* 下载完成弹窗 */}
            <ParcelsDownloadModal
                visible={downloadCompleteModalVisible}
                onClose={() => setDownloadCompleteModalVisible(false)}
                fileName={generatedFileName}
            />
        </>
    );
};

export default SlaDetailDrawer;
